/* eslint-disable no-return-assign */
import React, { memo, useCallback, useMemo, useState } from "react"
import { Appearance, InteractionManager, LayoutChangeEvent, LayoutRectangle, Platform, StatusBar } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import { ScreenProps } from "./screen.props"
import { isNonScrolling, presets } from "./screen.presets"
import { KeyboardAvoidingView, ScrollView, View } from "native-base"
import { color, nativeBaseTheme } from "../../theme"
import { useFocusEffect, useIsFocused, useRoute } from "@react-navigation/native"
import SystemNavigationBar from "react-native-system-navigation-bar"
// import { delay } from "../../utils/delay"
import { LazyRender } from "../lazy-render/lazy-render"

const ScreenWithoutScrolling = memo(function ScreenWithoutScrolling(props: ScreenProps) {
  const {
    colorScheme = Appearance.getColorScheme(), safeAreaEdges = ['bottom'],
    lazyLoad = true,
    hideOnBlurred = true
  } = props
  const isFocused = useIsFocused();
  const preset = presets(colorScheme).fixed
  const style = props.style || {}
  const backgroundStyle = props.backgroundColor ? { backgroundColor: props.backgroundColor } : {}

  const children = useMemo(() => {
    if (Platform.OS !== 'web' && hideOnBlurred && !isFocused) return null;
    return props.children
  }, [hideOnBlurred, isFocused, props.children])

  return (
    <KeyboardAvoidingView
      style={[preset.outer, backgroundStyle]}
      behavior={Platform.OS === 'ios' ? "padding" : undefined}
    >
      <SafeAreaView edges={props.unsafe ? ['left', 'right'] : safeAreaEdges} style={[preset.outer, backgroundStyle]}>
        <View testID={props.testID} style={[preset.inner, style]}>
          {lazyLoad ? <LazyRender>{children}</LazyRender> : children}
        </View>
      </SafeAreaView>
    </KeyboardAvoidingView>
  )
})

const ScreenWithScrolling = memo(function ScreenWithScrolling(props: ScreenProps) {
  const {
    colorScheme = Appearance.getColorScheme(), safeAreaEdges = ['bottom'], scrollViewProps,
    lazyLoad = true,
    hideOnBlurred = true, blurredBackgroundColor
  } = props
  const [ layout, setLayout ] = useState<LayoutRectangle>(null)
  const isFocused = useIsFocused();
  const preset = presets(colorScheme).scroll
  const style = props.style || {}
  const backgroundStyle = props.backgroundColor ? { backgroundColor: props.backgroundColor } : {}

  const children = useMemo(() => {
    if (Platform.OS !== 'web' && hideOnBlurred && !isFocused) return layout && <View
      height={`${layout.height}px`}
      width={`${layout.width}px`}
      backgroundColor={blurredBackgroundColor}
    />;
    return props.children
  }, [blurredBackgroundColor, hideOnBlurred, isFocused, layout, props.children])

  const onLayout: (event: LayoutChangeEvent) => void = useCallback((event) => {
    setLayout(event.nativeEvent.layout)
  }, [])

  return (
    <KeyboardAvoidingView
      style={[preset.outer, backgroundStyle]}
      behavior={Platform.OS === 'ios' ? "padding" : undefined}
    >
      <ScrollView
        ref={props.scrollViewRef}
        style={[preset.outer, backgroundStyle]}
        contentContainerStyle={[preset.inner, style]}
        keyboardShouldPersistTaps={props.keyboardShouldPersistTaps || "handled"}
        showsVerticalScrollIndicator={false}
        {...scrollViewProps}
      >
        <SafeAreaView
          testID={props.testID}
          edges={props.unsafe ? ['left', 'right'] : safeAreaEdges}
          style={[preset.inner, backgroundStyle]}
          onLayout={onLayout}
        >
          {lazyLoad ? <LazyRender>{children}</LazyRender> : children}
        </SafeAreaView>
      </ScrollView>
    </KeyboardAvoidingView>
  )
})

const systemNavigationBarStatus = {
  navigationColor: null,
  shown: null
}

export const updateSystemNavigatorBar = async ({
  colorScheme,
  hideNavigationBar,
  navigationBarBackgroundColor,
  backgroundColor,
  statusBarStyle, statusBarBackgroundColor
}) => {
  if (hideNavigationBar) {
    await SystemNavigationBar.navigationHide().catch()
    systemNavigationBarStatus.navigationColor = null;
    systemNavigationBarStatus.shown = null;
  }
  else {
    if (!systemNavigationBarStatus.shown) {
      await SystemNavigationBar.navigationShow().catch();
      systemNavigationBarStatus.shown = true;
    }
    const navigationColor = navigationBarBackgroundColor || backgroundColor || (colorScheme === "light" ? color.palette.white : nativeBaseTheme.colors.dark["50"])
    if (systemNavigationBarStatus.navigationColor !== navigationColor) {
      await SystemNavigationBar.setNavigationColor(navigationColor, false).catch();
      // await SystemNavigationBar.setNavigationBarContrastEnforced(true).catch();
      systemNavigationBarStatus.navigationColor = navigationColor;
    }
  }
  // await delay(10);
  await StatusBar.setBarStyle(statusBarStyle)
  if (Platform.OS === 'android')
    await StatusBar.setBackgroundColor(statusBarBackgroundColor)
}

/**
 * The starting component on every screen in the app.
 *
 * @param props The screen props
 */
export function Screen(props: ScreenProps) {
  const { colorScheme, hideNavigationBar, backgroundColor, navigationBarBackgroundColor } = props;
  const route = useRoute();
  const statusBarStyle = props.statusBar || (colorScheme === 'light' ? "dark-content" : "light-content")
  const statusBarBackgroundColor = props.backgroundBar || (nativeBaseTheme.colors[colorScheme]["50"])

  useFocusEffect(useCallback(() => {
    InteractionManager.runAfterInteractions(() => {
      updateSystemNavigatorBar({
        colorScheme,
        hideNavigationBar, navigationBarBackgroundColor,
        backgroundColor,
        statusBarStyle, statusBarBackgroundColor
      }).catch()
    })
  }, [backgroundColor, colorScheme, hideNavigationBar, navigationBarBackgroundColor, statusBarBackgroundColor, statusBarStyle]))

  if (isNonScrolling(props.preset)) {
    return <ScreenWithoutScrolling testID={`jala:screen/${route.name}`} {...props} />
  } else {
    return <ScreenWithScrolling testID={`jala:screen/${route.name}`} {...props} />
  }
}
